import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import React from 'react';
import {
    useEventBasicInformationContext,
    useTicketContext,
} from '../../../../contexts';
import { normalizeDisqualificationKeys } from '../../../../contexts/TicketContext/helpers';
import { useCondensedCheckout } from '../../../../experimentation/useCondensedCheckout';
import { SALES_STATUS } from '../../../../types';
import { useIntersecting } from '../../../hooks/useIntersecting';
import { TicketVariant } from '../../../Tickets';
import { getFormattedDateCard } from '../../../utils/localizedDates';
import { ConversionBar } from '../ConversionBar/ConversionBar';
import { ConversionBarContainerProps } from '../ConversionBarContainer';
import '../ConversionBarContainer.scss';
import { isCondensedCheckoutExcluded } from '../isCondensedCheckoutExcluded';
import { TicketSelection } from '../TicketSelection/TicketSelection';

export const ConversionBarContent: React.FC<ConversionBarContainerProps> = ({
    ...props
}) => {
    let enforceDateAndTimeVisualization = false;
    const { ticketData, isFetchComplete } = useTicketContext();
    const {
        salesStatus,
        selectedDate = false,
        isRepeating = false,
        start,
        locale,
    } = useEventBasicInformationContext();
    const { compactCheckoutDisqualifications } = ticketData;
    const isCondensedCheckoutExperimentEnabled = useCondensedCheckout();

    const useAllInPrice = ticketData.ticketClasses?.some((tc) =>
        tc.variants?.some((variant: TicketVariant) => variant.useAllInPrice),
    );

    const isExcludedFromCondensedCheckout = isCondensedCheckoutExcluded(
        normalizeDisqualificationKeys(
            compactCheckoutDisqualifications,
            salesStatus,
        ),
    );

    const hasTicketsData = ticketData.ticketClasses.length > 0;

    const shouldShowCondensedCheckout = isCondensedCheckoutExperimentEnabled;
    const shouldShowRegularConversionBar =
        !isCondensedCheckoutExperimentEnabled &&
        isExcludedFromCondensedCheckout;
    const shouldShowConversionBar =
        shouldShowCondensedCheckout || shouldShowRegularConversionBar;
    const shouldShowTicketSelection =
        hasTicketsData && !shouldShowConversionBar;

    const [isDateAndTimeVisible] = useIntersecting({
        domNode: HAS_WINDOW
            ? document.getElementById('instance-selector')
            : null,
        ratio: 1,
        stayActive: !selectedDate,
        rootMargin: '0px',
    });

    if (
        !isCondensedCheckoutExperimentEnabled &&
        !props.isTESessionSelectorExperimentEnabled &&
        isRepeating &&
        !isDateAndTimeVisible &&
        isFetchComplete &&
        salesStatus === SALES_STATUS.ON_SALE
    ) {
        enforceDateAndTimeVisualization = true;
    }

    if (!isFetchComplete) {
        return (
            <div className="express-checkout-progressbar">
                <ProgressIndicator style="gradient" size="medium-chunky" />
            </div>
        );
    }

    if (isFetchComplete && shouldShowTicketSelection) {
        return (
            <TicketSelection
                eventName={props.eventName}
                affiliateCode={props.affiliateCode}
                isPreview={props.isPreview}
                promoCode={props.promoCode}
                waitlistToken={props.waitlistToken}
                rsvpToken={props.rsvpToken}
                campaignToken={props.campaign_token}
                enforceDateAndTimeVisualization={
                    enforceDateAndTimeVisualization
                }
            />
        );
    }

    let panelDisplayMessage = '';
    if (props.isTESessionSelectorExperimentEnabled && start && locale) {
        const eventStartDateInfo = getFormattedDateCard({
            date: start,
            locale: locale,
        });

        panelDisplayMessage = `${eventStartDateInfo.monthAbbr} ${eventStartDateInfo.day}・${eventStartDateInfo.time}`;
    }

    if (isFetchComplete && shouldShowConversionBar) {
        return (
            <ConversionBar
                {...{
                    ...props,
                    panelDisplayPrice: ticketData.panelDisplayPrice,
                    panelDisplayMessage,
                    statusIsUnavailable:
                        salesStatus === SALES_STATUS.UNAVAILABLE,
                    salesStatus: salesStatus,
                    enforceDateAndTimeVisualization,
                    useAllInPrice,
                }}
            />
        );
    }

    return null;
};
